import { Loader2 } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'
import Cropper, { Area, Point } from 'react-easy-crop'
import { OutlinedButton, PrimaryButton } from 'src/component/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'src/component/ui/dialog'
import { Drawer, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle } from 'src/component/ui/drawer'
import { Input } from 'src/component/ui/input/input'
import { useMediaQuery } from 'src/hook/use-media-query'
import { useUploadProfilePhoto } from 'src/hook/use-upload-profile-photo'
import { logger } from 'src/util/logger'
import { toasterUtil } from 'src/util/toaster-util'
import 'react-easy-crop/react-easy-crop.css'

interface PhotoUploadDialogProps {
  isOpen: boolean
  onClose: () => void
}

const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image()
    image.setAttribute('crossOrigin', 'anonymous')
    image.onload = () => resolve(image)
    image.onerror = (error) => reject(error)
    image.src = url
  })
}

const getCroppedImg = async (imageSrc: string, pixelCrop: Area, rotation = 0): Promise<File> => {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    throw new Error('Could not get canvas context')
  }

  const maxSize = Math.max(image.width, image.height)
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2))

  canvas.width = safeArea
  canvas.height = safeArea

  ctx.translate(safeArea / 2, safeArea / 2)
  ctx.rotate((rotation * Math.PI) / 180)
  ctx.translate(-safeArea / 2, -safeArea / 2)

  ctx.drawImage(image, safeArea / 2 - image.width * 0.5, safeArea / 2 - image.height * 0.5)

  const data = ctx.getImageData(0, 0, safeArea, safeArea)

  canvas.width = pixelCrop.width
  canvas.height = pixelCrop.height

  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width * 0.5 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height * 0.5 - pixelCrop.y),
  )

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }
        const file = new File([blob], 'cropped-image.jpg', {
          type: 'image/jpeg',
          lastModified: Date.now(),
        })
        resolve(file)
      },
      'image/jpeg',
      0.95,
    )
  })
}

export const PhotoUploadDialog = ({ isOpen, onClose }: PhotoUploadDialogProps): React.ReactElement => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [rotation, setRotation] = useState(0)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null)
  const { mutateAsync: uploadProfilePhoto, isLoading } = useUploadProfilePhoto()
  const isMobile = useMediaQuery('(max-width: 556px)')

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string)
        setZoom(1)
        setRotation(0)
        setCrop({ x: 0, y: 0 })
      }
      reader.readAsDataURL(file)
    }
  }

  const onCropComplete = useCallback((_croppedArea: Area, croppedAreaPixels: Area): void => {
    setCroppedAreaPixels(croppedAreaPixels)
  }, [])

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile || !previewUrl || !croppedAreaPixels) {
      return
    }

    try {
      const file = await getCroppedImg(previewUrl, croppedAreaPixels, rotation)
      await uploadProfilePhoto(file)
      toasterUtil.success('Profile photo is updated successfully')
      handleClose()
    } catch (error) {
      toasterUtil.error('Failed to upload profile photo. Please try again.')
      logger.error(`Failed to upload profile photo, ${error}`)
    }
  }

  const handleClose = (): void => {
    setSelectedFile(null)
    setPreviewUrl(null)
    setZoom(1)
    setRotation(0)
    setCrop({ x: 0, y: 0 })
    setCroppedAreaPixels(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    onClose()
  }

  const handleSelectFile = (): void => {
    fileInputRef.current?.click()
  }

  const content = (
    <>
      <Input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
      <div>
        <div className="relative w-full h-[248px] bg-secondary-gray rounded-md overflow-hidden">
          {previewUrl ? (
            <Cropper
              image={previewUrl}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropSize={{ width: 200, height: 200 }}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <OutlinedButton size="sm" onClick={handleSelectFile} className="border-ivory text-ivory">
                Select Photo
              </OutlinedButton>
            </div>
          )}
        </div>
      </div>
    </>
  )

  const footer = (
    <div className="gap-3 w-full flex">
      <OutlinedButton size="sm" onClick={handleClose} className="flex-1 text-primary">
        Cancel
      </OutlinedButton>
      <PrimaryButton
        size="sm"
        onClick={handleUpload}
        disabled={!selectedFile || !croppedAreaPixels || isLoading}
        className="flex-1"
        isLoading={isLoading}
      >
        Save
      </PrimaryButton>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DrawerContent className="bg-primary-gray px-6 pb-6">
          <DrawerHeader>
            <DrawerTitle className="font-evogria text-4xl text-ivory uppercase font-normal">Change Photo</DrawerTitle>
            <DrawerDescription className="text-ivory font-roboto-condensed font-base mt-3">
              Choose a photo and crop it to your profile picture
            </DrawerDescription>
          </DrawerHeader>
          <div className="px-4">{content}</div>
          <DrawerFooter className="gap-3">{footer}</DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-evogria text-4xl text-ivory uppercase font-normal">Change Photo</DialogTitle>
          <DialogDescription className="text-ivory font-roboto-condensed font-base mt-3">
            Choose a photo and crop it to your profile picture
          </DialogDescription>
        </DialogHeader>
        <div>{content}</div>
        <DialogFooter className="gap-3 w-full">{footer}</DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
